import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Button from '../../shared/components/ui/Button';
import AddEditHolidays from '../components/forms/AddEditHolidays';
import Modal from '../../shared/components/ui/Modal';
import noHoliday from '../../shared/assets/svg/NoHoliday.svg';
import {
  createHoliday,
  getAllHoliday,
  updateHoliday,
  addNonWorkingDay
} from '../services/api/implementation/impl';
import toast from 'react-hot-toast';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const DAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

const normalizeDate = (d) => {
  const n = new Date(d);
  n.setHours(0, 0, 0, 0);
  return n;
};

const MonthCalendar = ({ year, month, holidayMap, onHolidayClick, onEmptyDateClick }) => {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const cells = [];

  for (let i = 0; i < firstDay; i++) {
    cells.push(<div key={`empty-${i}`} />);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const holiday = holidayMap[dateKey];
    const isHoliday = !!holiday;

    cells.push(
      <div key={day} className="flex items-center justify-center relative group">
        {isHoliday ? (
          <div
            onClick={() => onHolidayClick(holiday.id)}
            className="w-9 h-9 rounded-full border-[2.5px] border-[#00BC7D] flex items-center justify-center cursor-pointer p-[3px] hover:border-[#00956B] transition-colors">
            <div className="w-full h-full rounded-full bg-[#00BC7D] flex items-center justify-center group-hover:bg-[#00BC7D] transition-colors">
              <svg width="12" height="10" viewBox="0 0 14 11" fill="none">
                <path d="M1.5 5.5L5 9L12.5 1.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-[11px] rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
              {holiday.name} <span className="text-gray-300 ml-1">• Click to edit</span>
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-transparent border-t-gray-800" />
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onEmptyDateClick?.(dateKey)}
            className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-[13px] text-gray-500 font-medium hover:border-[#00BC7D] hover:text-[#00BC7D] hover:bg-[#ECFDF5]/80 transition-colors cursor-pointer">
            {day}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-6 py-5">
      <h3 className="text-lg font-bold text-gray-800 pb-3 mb-4 border-b border-gray-200">
        {MONTH_NAMES[month]} {year}
      </h3>
      <div className="grid grid-cols-7 gap-x-2 gap-y-3 mb-3">
        {DAY_LABELS.map((d) => (
          <div key={d} className="flex items-center justify-center text-[11px] font-semibold text-gray-400 tracking-wide">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-x-2 gap-y-3">{cells}</div>
    </div>
  );
};

const HolidayBalanceCard = ({ label, count, subtitle }) => (
  <div className="min-w-[220px] max-w-[280px] bg-[#ECFDF5] rounded-xl border border-[#00BC7D] overflow-hidden flex">
    <div className="px-5 py-4">
      <div className="flex items-center gap-2.5 mb-2">
        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center self-center">
          <img
            src={`${process.env.PUBLIC_URL || ''}/selected_dates.png`}
            alt=""
            className="max-h-5 max-w-5 h-auto w-auto object-contain block align-middle"
          />
        </span>
        <span className="text-[11px] font-bold uppercase tracking-wide text-gray-600 leading-5 self-center">
          {label}
        </span>
      </div>
      <p className="text-3xl font-bold text-green-700">
        {count} <span className="text-sm font-normal text-gray-500">days</span>
      </p>
      <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
    </div>
  </div>
);

const ConfigureHolidays = ({ embedded = false }) => {
  const userId = Cookies.get('userId');
  const [isOpenAddEditModal, setIsOpenAddEditModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const { collapsed } = useSidebar();
  const [selectedHoliday, setSelectedHoliday] = useState();
  const [prefillStartDate, setPrefillStartDate] = useState(null);
  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());

  const [isOpenNonWorkingModal, setIsOpenNonWorkingModal] = useState(false);
  const [selectedWeekday, setSelectedWeekday] = useState(null);
  const [isSavingNonWorking, setIsSavingNonWorking] = useState(false);

  const weekDays = [
    { label: 'Sunday', value: 0 },
    { label: 'Monday', value: 1 },
    { label: 'Tuesday', value: 2 },
    { label: 'Wednesday', value: 3 },
    { label: 'Thursday', value: 4 },
    { label: 'Friday', value: 5 },
    { label: 'Saturday', value: 6 }
  ];

  const { mutateAsync: addNonWorkingDayAsync } = useMutation({
    mutationFn: (payload) => addNonWorkingDay(payload)
  });

  const handleSaveNonWorkingDays = async () => {
    if (selectedWeekday === null || selectedWeekday === undefined) {
      toast.error('Please select a day');
      return;
    }
    try {
      setIsSavingNonWorking(true);
      await addNonWorkingDayAsync({ day: selectedWeekday });
      toast.success('Non-working day configured successfully');
      setIsOpenNonWorkingModal(false);
      setSelectedWeekday(null);
      refetchHolidays();
    } catch (err) {
      console.error('Error configuring non-working days', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to configure non-working days'
      );
    } finally {
      setIsSavingNonWorking(false);
    }
  };

  const {
    data: allHolidays,
    refetch: refetchHolidays,
    isFetching
  } = useQuery({
    queryKey: ['getAllHoliday'],
    queryFn: () => getAllHoliday(),
    keepPreviousData: true
  });

  useEffect(() => {
    mixpanel.track('Configure Holidays Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    refetchHolidays();
  }, []);

  useEffect(() => {
    if (allHolidays?.data) {
      setHolidays([...allHolidays.data].reverse());
    }
  }, [allHolidays?.data]);

  const { mutate: handleCreateHoliday } = useMutation({
    mutationFn: (payload) => {
      if (modalType === 'Add') {
        return createHoliday(payload);
      } else if (modalType === 'Edit') {
        return updateHoliday(selectedHoliday.id, payload);
      }
      throw new Error('Invalid modal type');
    },
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success(`Holiday ${modalType === 'Add' ? 'created' : 'updated'} successfully`);
      refetchHolidays();
      setIsOpenAddEditModal(false);
      setPrefillStartDate(null);
      setModalType('');
      setSelectedHoliday();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error(`Error ${modalType === 'Add' ? 'creating' : 'updating'} holidays`, err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          `Error ${modalType === 'Add' ? 'creating' : 'updating'} holidays`
      );
    }
  });

  const handleAddHoliday = (data) => {
    const payload = modalType === 'Add' ? data : data.holidays?.[0];
    handleCreateHoliday(payload);
  };

  const yearHolidays = useMemo(() => {
    return holidays.filter((h) => {
      const start = new Date(h.start_date);
      const end = new Date(h.end_date);
      return start.getFullYear() === displayYear || end.getFullYear() === displayYear;
    });
  }, [holidays, displayYear]);

  const holidayMap = useMemo(() => {
    const map = {};
    yearHolidays.forEach((h) => {
      const start = normalizeDate(h.start_date);
      const end = normalizeDate(h.end_date);
      const cur = new Date(start);
      while (cur <= end) {
        if (cur.getFullYear() === displayYear) {
          const key = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
          map[key] = { name: h.ocassion || 'Holiday', id: h.id };
        }
        cur.setDate(cur.getDate() + 1);
      }
    });
    return map;
  }, [yearHolidays, displayYear]);

  const totalDays = Object.keys(holidayMap).length;

  const handleHolidayClick = (id) => {
    const holiday = holidays.find((item) => item.id === Number(id));
    if (holiday) {
      setPrefillStartDate(null);
      setModalType('Edit');
      setSelectedHoliday(holiday);
      setIsOpenAddEditModal(true);
    }
  };

  const handleEmptyDateClick = (dateKey) => {
    setSelectedHoliday(undefined);
    setPrefillStartDate(dateKey);
    setModalType('Add');
    setIsOpenAddEditModal(true);
  };

  const closeAddEditModal = () => {
    setIsOpenAddEditModal(false);
    setPrefillStartDate(null);
  };

  const renderContent = () => (
    <>
      {/* Holiday Balance */}
      <div className="rounded-2xl border border-gray-100 bg-white p-5 mb-6 shadow-sm">
        <h2 className="text-base font-bold text-gray-800 mb-4">Holiday Balance</h2>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <HolidayBalanceCard
            label="Selected Holidays"
            count={totalDays}
            subtitle={`Days off in ${displayYear}`}
          />
          <div className="flex gap-2">
            <Button
              displayType="secondary"
              onClick={() => setIsOpenNonWorkingModal(true)}>
              Add Non Working day
            </Button>
            <Button
              onClick={() => {
                setSelectedHoliday(undefined);
                setPrefillStartDate(null);
                setModalType('Add');
                setIsOpenAddEditModal(true);
              }}>
              Configure Holidays
            </Button>
          </div>
        </div>
      </div>

      {/* Year Navigation */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setDisplayYear((y) => y - 1)}
          className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm">
          <span className="text-base">‹</span> Previous
        </button>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800">Year {displayYear}</h2>
          <p className="text-xs text-gray-400 mt-0.5">Holidays highlighted on the calendar</p>
        </div>
        <button
          onClick={() => setDisplayYear((y) => y + 1)}
          className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm">
          Next <span className="text-base">›</span>
        </button>
      </div>

      {/* Monthly Calendars */}
      {holidays.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 pb-8">
          {Array.from({ length: 12 }, (_, i) => (
            <MonthCalendar
              key={i}
              year={displayYear}
              month={i}
              holidayMap={holidayMap}
              onHolidayClick={handleHolidayClick}
              onEmptyDateClick={handleEmptyDateClick}
            />
          ))}
        </div>
      ) : (
        <div className="h-[40vh] w-full flex justify-center items-center">
          <img className="h-[20rem] w-[20rem]" src={noHoliday} alt="No holidays configured" />
        </div>
      )}
    </>
  );

  return (
    <>
      {isOpenNonWorkingModal && (
        <Modal
          isDirty={false}
          confirmOnClose={false}
          width={40}
          name={'Configure Non-Working Days'}
          onClose={() => setIsOpenNonWorkingModal(false)}>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-600">Select the weekly off day:</p>
            <div className="grid grid-cols-2 gap-3">
              {weekDays.map((d) => (
                <label key={d.value} className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="weekday"
                    checked={selectedWeekday === d.value}
                    onChange={() => setSelectedWeekday(d.value)}
                  />
                  <span>{d.label}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <Button displayType="secondary" onClick={() => setIsOpenNonWorkingModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveNonWorkingDays} disabled={isSavingNonWorking}>
                {isSavingNonWorking ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {isOpenAddEditModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={56}
          name={` ${modalType === 'Add' ? 'Configure Holidays' : 'Edit Holiday'}`}
          onClose={closeAddEditModal}>
          <AddEditHolidays
            isSubmitting={isSubmitting}
            onSubmit={handleAddHoliday}
            onClose={closeAddEditModal}
            modalType={modalType}
            setModalType={setModalType}
            selectedHoliday={selectedHoliday}
            initialStartDate={modalType === 'Add' ? prefillStartDate : null}
          />
        </Modal>
      )}

      {embedded ? (
        <div className="w-full pt-8 px-6 pb-8">
          {renderContent()}
        </div>
      ) : (
        <Layout>
          <div
            className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
              collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
            }`}>
            <Header data={{ name: 'Holidays' }} />
          </div>

          <GridWrapper width={100}>
            <div className="mt-[6rem] pb-8">
              {renderContent()}
            </div>
          </GridWrapper>
        </Layout>
      )}
    </>
  );
};

export default ConfigureHolidays;
